import React, { useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { stringify } from 'query-string';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Badge } from '@dashboard/components/ui/badge';
import { fetcher, fetchInfinite } from '@/dashboard/services/request';
import { CircleCheck } from 'lucide-react';
import { OrderCardMenu } from '@dashboard/components/OrderPage';
import {
  CancelChargeFormDialog,
  cancelOffReason,
} from '@dashboard/components/OrderPage/CancelChargeDialog';
import { toast } from '@dashboard/context/ToastContext';
import { chargeKey } from '@dashboard/lib/queryKeys';
import getPriceLabel from '@/dashboard/services/translation/getPriceLabel';
import { formatDateTime } from '@/dashboard/lib/format-date';
import StatusBadge from '@dashboard/components/StatusBadge';
import TicketCode from '@dashboard/components/TicketCode';
import { cn } from '@dashboard/lib/utils';

const GUEST_DETAILS_BADGES = {
  missing: { status: 'unpaid', label: 'Missing details' },
  flagged: { status: 'unpaid', label: 'Review details' },
  added: { status: 'hidden', label: 'Complete' },
};

const getStatusBadgeAppearance = status => {
  if (status === 'PAID') return 'success';
  if (status === 'CANCELED') return 'destructive';
  return 'info';
};

const formatStatus = status => {
  if (status === 'PAID') return 'Valid';
  if (status === 'CANCELED') return 'Canceled';
  return status;
};

const muted = 'text-sm text-gray-500 dark:text-muted-foreground';

// A ticket on a booking that collects guests shows its attendee, or none yet; the booker sits under it.
const sameBooking = (a, b) =>
  Boolean(a?.original.orderId) && a.original.orderId === b?.original.orderId;

// Joins only neighbouring rows, so a sort or filter that splits a booking simply shows no line.
export function BookingThread({ row, table }) {
  const rows = table.getRowModel().rows;
  const index = rows.findIndex(other => other.id === row.id);
  const above = sameBooking(rows[index - 1], row);
  const below = sameBooking(rows[index + 1], row);
  if (!above && !below) return null;
  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-1.5 w-1.5">
      <span
        className={cn(
          'absolute left-[2.5px] w-px bg-gray-200 dark:bg-border',
          above ? 'top-0' : 'top-1/2',
          below ? 'bottom-0' : 'bottom-1/2',
        )}
      />
      {above && below ? null : (
        <span className="absolute left-0 top-1/2 size-1.5 -translate-y-1/2 rounded-full bg-gray-300 dark:bg-muted-foreground/40" />
      )}
    </span>
  );
}

function AttendeeCell({ guest }) {
  const { attendee, bookedBy } = guest;
  const booker = bookedBy?.name || bookedBy?.email;
  const canceled = guest.status === 'CANCELED';
  return (
    <div>
      {attendee ? (
        <>
          <div className={canceled ? muted : 'font-medium'}>{attendee.name || attendee.email}</div>
          {attendee.name && attendee.email ? <div className={muted}>{attendee.email}</div> : null}
        </>
      ) : (
        <div className={muted}>{attendee === null ? 'Not named yet' : guest.customerName}</div>
      )}
      {booker ? (
        <div className="text-xs text-gray-500 dark:text-muted-foreground">Booked by {booker}</div>
      ) : null}
    </div>
  );
}

export default function GuestsList({
  sort,
  filter = {},
  search,
  sorting,
  onSortingChange,
  collectsGuests = false,
}) {
  const { page } = usePage();
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const [canceling, setCanceling] = useState(null);

  // The row has no booking state, so the booking is checked before the dialog opens.
  const startCancel = async guest => {
    try {
      const booking = await queryClient.fetchQuery({
        queryKey: chargeKey(guest.bookingId),
        queryFn: () => fetcher(`/sellers/${sellerId}/charges/${guest.bookingId}`, token),
      });
      const off = cancelOffReason(booking, 'booking');
      if (off) {
        toast({ title: off, type: 'error' });
        return;
      }
      setCanceling({ ticketId: guest._id, booking });
    } catch (error) {
      toast({ title: "Couldn't load this booking", description: error?.message, type: 'error' });
    }
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: ['pageCustomers', sellerId, sort, search, filter, page._id],
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) => {
        return fetchInfinite(
          `/sellers/${sellerId}/pages/${page._id}/guests?${stringify({
            filter: JSON.stringify(filter),
            search,
            limit: 10,
            page: pageParam,
            sort: JSON.stringify(sort),
          })}`,
          token,
        );
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.data.has_more) return undefined;
        return allPages.length;
      },
    });

  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data.guestList) ?? [], [data]);

  const columns = useMemo(
    () => [
      collectsGuests
        ? {
            id: 'attendee',
            accessorFn: guest => guest.attendee?.name ?? '',
            header: ({ column }) => <DataGridColumnHeader column={column} title="Attendee" />,
            cell: ({ row, table }) => (
              <>
                <BookingThread row={row} table={table} />
                <AttendeeCell guest={row.original} />
              </>
            ),
            meta: { className: 'relative' },
            enableSorting: true,
          }
        : {
            accessorKey: 'customerEmail',
            header: ({ column }) => <DataGridColumnHeader column={column} title="Guest" />,
            cell: ({ row, table }) => {
              const guest = row.original;
              return (
                <div>
                  <BookingThread row={row} table={table} />
                  <div className={guest.status === 'CANCELED' ? muted : 'font-medium'}>
                    {guest.customerEmail || ''}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-muted-foreground">
                    {guest.customerName || ''}
                  </div>
                </div>
              );
            },
            meta: { className: 'relative' },
            enableSorting: true,
          },
      {
        id: 'ticketType',
        header: 'Ticket',
        cell: ({ row }) => (
          <div className="overflow-hidden text-ellipsis whitespace-nowrap max-w-xs">
            {row.original.ticketType?.name || ''}
          </div>
        ),
        enableSorting: false,
      },
      ...(collectsGuests
        ? [
            {
              id: 'guestDetails',
              header: 'Attendee details',
              cell: ({ row }) => {
                const badge = GUEST_DETAILS_BADGES[row.original.guestDetails];
                return badge ? <StatusBadge {...badge} /> : null;
              },
              enableSorting: false,
            },
          ]
        : []),
      {
        accessorKey: 'ticketShortId',
        header: 'Ticket code',
        cell: ({ row }) => <TicketCode code={row.original.ticketShortId} />,
        enableSorting: false,
      },
      {
        accessorKey: 'originalPrice',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Price" />,
        cell: ({ row }) => {
          const guest = row.original;
          return (
            <span className="font-medium">
              {guest.originalPrice === 0 || !guest.originalPrice
                ? 'Free'
                : getPriceLabel({
                    amount: guest.originalPrice,
                    currency: guest.currency || page.currency,
                  })}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'orderedAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Booked on" />,
        cell: ({ row }) => formatDateTime(row.original.orderedAt),
        enableSorting: true,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const guest = row.original;
          return (
            <Badge variant={getStatusBadgeAppearance(guest.status)}>
              {formatStatus(guest.status)}
            </Badge>
          );
        },
        enableSorting: false,
      },
      {
        id: 'checkedIn',
        header: 'Checked in',
        cell: ({ row }) => {
          const guest = row.original;
          if (guest.status === 'CANCELED') {
            return <Badge variant="warning">Void</Badge>;
          }
          if (guest.latestCheckIn != null && guest.latestCheckIn.status === 'CHECKEDIN') {
            return (
              <Badge variant="success">
                <CircleCheck className="h-3 w-3 mr-1" aria-hidden="true" />
                Checked in
              </Badge>
            );
          }
          return <Badge variant="info">Pending</Badge>;
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) =>
          row.original.bookingId ? (
            <div className="flex justify-end" onClick={event => event.stopPropagation()}>
              <OrderCardMenu
                label="Ticket options"
                items={[
                  {
                    label: 'Cancel ticket',
                    destructive: true,
                    disabled: row.original.status === 'CANCELED',
                    onSelect: () => startCancel(row.original),
                  },
                ]}
              />
            </div>
          ) : null,
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startCancel only reads sellerId and token
    [page.currency, collectsGuests, sellerId, token],
  );

  if (isError) {
    return <div className="m-4">Unable to show guests. Please refresh or contact support.</div>;
  }

  const isEmpty = !isLoading && flatData.length === 0;

  const emptyState = isEmpty ? (
    <EmptyState>
      <EmptyState.Title>No guests yet</EmptyState.Title>
      <EmptyState.Subtitle>Your guests will show up here.</EmptyState.Subtitle>
    </EmptyState>
  ) : undefined;

  return (
    <>
      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={onSortingChange}
        getRowHref={guest => {
          if (collectsGuests) return `/attendees/${guest._id}`;
          return guest.customer ? `/customers/${guest.customer}` : undefined;
        }}
        emptyState={emptyState}
        emptyMessage="No guests found"
      />
      {canceling ? (
        <CancelChargeFormDialog
          payment={canceling.booking}
          noun="booking"
          open
          onOpenChange={next => !next && setCanceling(null)}
          ticketId={canceling.ticketId}
        />
      ) : null}
    </>
  );
}
